import { useEffect, useState } from 'react'
import { listSessions } from '~/api/sessions'
import type { AdminSession } from '~/api/types'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
    DataListTable,
    type DataListColumn,
} from '~/components/ui/data-list-table'
import { Input } from '~/components/ui/input'
import { SearchInput } from '~/components/ui/search-input'
import { Select } from '~/components/ui/select'
import { formatDateTime, sessionUserLabel } from '~/lib/utils'

// 세션 목록 테이블. 상태/기간 필터 + limit/offset 페이지네이션 + 행 클릭 콜백.
const PAGE_SIZE = 20

const STATUS_OPTIONS = [
    { value: '', label: '전체 상태' },
    { value: 'active', label: '진행 중' },
    { value: 'completed', label: '완료' },
    { value: 'archived', label: '보관' },
]

const STATUS_TONES: Record<string, BadgeTone> = {
    active: 'info',
    completed: 'success',
    archived: 'neutral',
}

const COLUMNS: DataListColumn<AdminSession>[] = [
    {
        key: 'createdAt',
        header: '생성일',
        render: (s) => formatDateTime(s.createdAt),
    },
    {
        key: 'title',
        header: '제목',
        className: 'max-w-[16rem]',
        render: (s) => (
            <span className='block max-w-full truncate'>
                {s.title || '제목 없음'}
            </span>
        ),
    },
    {
        key: 'status',
        header: '상태',
        render: (s) => (
            <Badge tone={STATUS_TONES[s.status] ?? 'neutral'}>{s.status}</Badge>
        ),
    },
    {
        key: 'user',
        header: '사용자',
        render: (s) => sessionUserLabel(s),
    },
    {
        key: 'profile',
        header: '프로필',
        render: (s) => s.profile,
    },
]

export function LogTable({
    profile,
    onSelect,
    selectedId,
}: {
    profile: string
    onSelect: (session: AdminSession) => void
    selectedId?: string
}) {
    const [sessions, setSessions] = useState<AdminSession[]>([])
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [status, setStatus] = useState('')
    const [q, setQ] = useState('')
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [offset, setOffset] = useState(0)

    useEffect(() => {
        let alive = true
        setLoading(true)
        setError(null)
        listSessions({
            profile,
            status: status || undefined,
            q: q.trim() || undefined,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
            limit: PAGE_SIZE,
            offset,
        })
            .then((data) => {
                if (alive) setSessions(data)
            })
            .catch((e) => {
                if (alive) setError(e.message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [profile, status, q, dateFrom, dateTo, offset])

    function resetOffset(fn: () => void) {
        setOffset(0)
        fn()
    }

    return (
        <div>
            <div className='mb-4 flex flex-wrap items-end gap-3'>
                <label className='flex flex-col gap-1'>
                    <span className='krds-label'>상태</span>
                    <Select
                        className='h-9 w-40'
                        value={status}
                        onChange={(e) =>
                            resetOffset(() => setStatus(e.target.value))
                        }>
                        {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </Select>
                </label>
                <label className='flex flex-col gap-1'>
                    <span className='krds-label'>시작일</span>
                    <Input
                        type='date'
                        className='h-9 w-40'
                        value={dateFrom}
                        onChange={(e) =>
                            resetOffset(() => setDateFrom(e.target.value))
                        }
                    />
                </label>
                <label className='flex flex-col gap-1'>
                    <span className='krds-label'>종료일</span>
                    <Input
                        type='date'
                        className='h-9 w-40'
                        value={dateTo}
                        onChange={(e) =>
                            resetOffset(() => setDateTo(e.target.value))
                        }
                    />
                </label>
                <label className='flex flex-col gap-1'>
                    <span className='krds-label'>검색</span>
                    <SearchInput
                        className='w-72'
                        value={q}
                        onChange={(v) => resetOffset(() => setQ(v))}
                        placeholder='제목·사용자 검색'
                    />
                </label>
            </div>

            <DataListTable
                items={sessions}
                loading={loading}
                error={error}
                columns={COLUMNS}
                getRowId={(s) => s.id}
                onRowClick={onSelect}
                selectedId={selectedId}
                empty='세션이 없어요.'
            />

            <div className='mt-4 flex items-center justify-between'>
                <span className='text-muted-foreground text-xs'>
                    {offset + 1}–{offset + sessions.length}
                </span>
                <div className='flex gap-2'>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={offset === 0}
                        onClick={() =>
                            setOffset((o) => Math.max(0, o - PAGE_SIZE))
                        }>
                        이전
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={sessions.length < PAGE_SIZE}
                        onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                        다음
                    </Button>
                </div>
            </div>
        </div>
    )
}
